import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Menu from 'components/Menu/Menu';
import MenuItem from 'components/Menu/MenuItem';
import DateRangePicker from 'components/forms/DateRangePicker';
import moment from 'moment';
import classNames from 'classnames';
import Calendar from 'icons/calendar';
import { makeStyles } from '@material-ui/styles';
import { DATE_FORMAT_SLASHES } from 'constants/config';

const useStyles = makeStyles(theme => ({
  dateSelectorBtn: {
    height: '100%',
    width: '100%',
    fontFamily: theme.typography.fontFamily,
    padding: '0.063rem 0.813rem 0.063rem 0.563rem',
    border: `1px solid ${theme.palette.secondary['300']}`,
    borderRadius: '0.375rem',
    color: theme.palette.secondary['900'],
    backgroundColor: theme.palette.white,
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '&.open': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
}));


const SelectDateWithCustomRangePicker = ({
  dateSelector, dateOptions, onChangeDate, onDateChangeFilter, startDate, endDate,
}) => {
  const CUSTOM_DATE_OPTION = { label: 'Custom', value: 'custom_date_range' };
  const classes = useStyles();
  const [anchorDateMenu, setAnchorDateMenu] = useState(null);
  const [showDatepicker, setShowDatePicker] = useState(false);


  const getDateMenuLabel = (selector, start, end) => {
    if (selector.value !== CUSTOM_DATE_OPTION.value) {
      return selector.label;
    } else if (start && end && selector.value === CUSTOM_DATE_OPTION.value) {
      return `${start.format(DATE_FORMAT_SLASHES)} - ${end.format(DATE_FORMAT_SLASHES)}`;
    }
    return 'Start date ~ End date';
  };

  const handleDateMenuClose = () => {
    setAnchorDateMenu(null);
  };

  const handleDateChangeFilter = (selector) => {
    setAnchorDateMenu(null);
    if (selector.value === CUSTOM_DATE_OPTION.value) setShowDatePicker(!showDatepicker);
    onDateChangeFilter(selector);
  };


  const handleChangeDate = (start, end) => {
    if ((start && end) || (!start && !end)) {
      setShowDatePicker(!showDatepicker);
    }
    onChangeDate(start, end);
  };

  const handleDateMenuClick = (event) => {
    setAnchorDateMenu(event.currentTarget);
  };

  const handleOnCloseDateRange = ({ start, end }) => {
    if (!start || !end) {
      setShowDatePicker(false);
    }
  };

  if (!showDatepicker) {
    return (
      <React.Fragment>
        <button
          className={classNames(classes.dateSelectorBtn, {
            open: anchorDateMenu,
          })}
          onClick={handleDateMenuClick}
        >
          <Calendar className={classes.filterIcon} viewBox="0 0 17 17" />
          {getDateMenuLabel(
            dateSelector,
            startDate,
            endDate,
          )}
        </button>
        <Menu
          id="ds-date-menu"
          anchorEl={anchorDateMenu}
          keepMounted
          open={Boolean(anchorDateMenu)}
          onClose={handleDateMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          {dateOptions.map(option => (
            <MenuItem
              onClick={() => handleDateChangeFilter(option)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </React.Fragment>
    );
  }
  return (
    <DateRangePicker
      initialStartDate={startDate}
      initialEndDate={endDate}
      onChange={handleChangeDate}
      onClose={handleOnCloseDateRange}
      minDate={null}
      maxDate={moment()}
    />
  );
};

SelectDateWithCustomRangePicker.propTypes = {
  dateSelector: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
  dateOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  onChangeDate: PropTypes.func.isRequired,
  onDateChangeFilter: PropTypes.func.isRequired,
  startDate: PropTypes.instanceOf(moment),
  endDate: PropTypes.instanceOf(moment),
};

SelectDateWithCustomRangePicker.defaultProps = {
  startDate: null,
  endDate: null,
};

export default SelectDateWithCustomRangePicker;
